import {
  Form,
  LargeInput,
  ValidationContainer,
  ValidationMessage,
  LargePhoneNumberInput,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useMemo,
  useState,
} from "react"
import type {
  ChangeEventHandler,
  FormEventHandler,
  ReactNode,
} from "react"
import { isSupportedCountry } from "react-phone-number-input"

import styles from "../AccountForms.module.scss"
import { RequirementLabel } from "../RequirementLabel"
import {
  CountryCodeSelect,
  defaultCountryCode,
} from "src/components/CountryCodeSelect"

import {
  useCountrySelectionContext,
  usePolyglot,
} from "src/contexts"

const polyglotPrefix = "components.account_forms.account_information."
const emailId = "email-address-id"
const phoneId = "phone-number-id"

const emailErrorId = "email-address-error-id"
const phoneErrorId = "phone-number-error-id"

interface AccountInformation {
  emailAddress: string;
  phoneNumber: string;
}

interface Props {
  form: AccountInformation;
  updateForm: (form: Partial<AccountInformation>) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  children?: ReactNode;
  disableEmail?: boolean;
}

export function AccountInformationForm(props: Props) {
  const {
    disableEmail = false,
    children,
    onSubmit,
    form,
    updateForm,
  } = props

  const polyglot = usePolyglot()
  const { country } = useCountrySelectionContext()

  const initialCountryCode: string = useMemo(
    () => {
      if (!country?.phone_supported) {
        return defaultCountryCode
      }

      const currentValue = country.country_code

      if (isSupportedCountry(currentValue)) {
        return currentValue
      }

      return defaultCountryCode
    },
    [country],
  )

  const [
    countryCode,
    setCountryCode,
  ] = useState<string>(initialCountryCode)

  const onEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        emailAddress: e.target.value,
      })
    },
    [updateForm],
  )

  const onPhoneChange = useCallback(
    (value?: string) => {
      updateForm({
        phoneNumber: value ?? "",
      })
    },
    [updateForm],
  )

  return (
    <Form
      className={styles.form}
      onSubmit={onSubmit}
    >
      <div
        className={styles.heading}
        id="account-info-heading"
      >
        {polyglot.t(`${polyglotPrefix}title`)}
      </div>
      <div className={styles.inputs}>
        <RequirementLabel variant="required" />
        <ValidationContainer>
          <LargeInput
            id={emailId}
            placeholder={polyglot.t(`${polyglotPrefix}email.placeholder`)}
            className={styles.input}
            type="email"
            value={form.emailAddress}
            onChange={onEmailChange}
            autoComplete="email"
            disabled={disableEmail}
            required
          />
          <ValidationMessage
            id={emailErrorId}
            invalidMessage={{
              typeMismatch: polyglot.t("validation_message.email_type_mismatch"),
            }}
          />
        </ValidationContainer>
        <RequirementLabel variant="optional" />
        <CountryCodeSelect
          large
          countryCode={countryCode}
          setCountryCode={setCountryCode}
        />
        <ValidationContainer>
          <LargePhoneNumberInput
            id={phoneId}
            country={countryCode}
            placeholder={polyglot.t(`${polyglotPrefix}phone.placeholder`)}
            className={styles.input}
            value={form.phoneNumber}
            onChange={onPhoneChange}
            autoComplete="tel"
          />
          <ValidationMessage
            id={phoneErrorId}
            invalidMessage={{
              customError: polyglot.t("validation_message.phone_invalid"),
            }}
          />
        </ValidationContainer>
      </div>
      {children}
    </Form>
  )
}
